import {Observable} from 'rxjs';
import moment from 'moment';
import {Address2ComponentValue} from './component/address2-component.domain';
import {EntityTypeCompanyOption, EntityTypes, EntityTypeValue, EntityTypeValueOptions} from './component/entity-type-component.domain';
import {PartnerValue} from './component/partner-component.domain';
import {TrusteeValue} from './component/trustee-component.domain';
import {CommercialEntityIntegrityErrorCode, validateKybPartyIntegrity} from '../utils/application-parties-utils';
import {compareIdentity} from '../utils/individual-identity';

/** Ownership % at or above which a company shareholder is a mandatory (locked) selection. */
export const KYB_MAJOR_SHAREHOLDER_THRESHOLD = 25;

/** Guarantor ownership coverage below which a non-blocking manual-review warning is shown. */
export const KYB_GUARANTEE_COVERAGE_THRESHOLD = 50;

/** ASIC organisation type code for an Australian public company; exempt from the guarantor rules. */
export const KYB_PUBLIC_COMPANY_TYPE_CODE = 'APUB';

export const KYB_MIN_SIGNERS = 2;

/** Minimum partners (individuals or entities) required on a partnership. */
export const KYB_MIN_PARTNERS = 2;

export type KybEntityMode = 'AUTOMATIC' | 'MANUAL';

export type KybReconciliationStatus = 'REVIEW_REQUIRED' | 'RECONCILED';
export type KybReconciliationAction = 'LINK' | 'ADD' | 'KEEP' | 'REMOVE' | 'IGNORE';

/**
 * Serializable full profile for one person entered through the manual key-people form.
 * File bytes are uploaded through the existing page document flow and are never included here.
 */
export interface KybManualPerson {
  clientId: string;
  individualId: number | null;
  applicationIndividualRoleId: number | null;
  commercialEntityId: number;
  title: string | null;
  givenName: string;
  middleName: string | null;
  surname: string;
  dateOfBirth: string;
  email: string;
  mobileNumber: string;
  hasAlias: boolean;
  aliasNames: string | null;
  countryOfCitizenship: string | null;
  residentialAddress: Address2ComponentValue;
  propertyOwner: boolean;
  propertyValue: number | null;
  mortgageBalance: number | null;
  propertyOwnedAddress: Address2ComponentValue;
  identificationType: string | null;
  identificationNumber: string | null;
  idDocumentProvided: boolean;
  isSigner: boolean;
  isGuarantor: boolean;
}

export interface KybManualEntityValue {
  commercialEntityId: number;
  people: KybManualPerson[];
}

/** One application individual eligible for reconciliation against this entity's KYB result. */
export interface KybReconciliationApplicationPerson {
  individualId: number;
  clientId: string;
  /** The individual's role for the reviewed entity, or null when they only belong elsewhere. */
  applicationIndividualRoleId: number | null;
  givenName: string;
  middleName: string | null;
  surname: string;
  dateOfBirth: string | null;
}

/** One unresolved side of the live comparison. Exact one-to-one pairs are resolved automatically. */
export interface KybReconciliationComparison {
  applicationIndividualRoleId: number | null;
  applicationKybStakeholderId: number | null;
}

/** A unique identity pair linked automatically as soon as reconciliation compares the entity. */
export interface KybReconciliationAutomaticLink {
  applicationIndividualRoleId: number;
  applicationKybStakeholderId: number;
}

/** Immutable applied action; automatic links may be stored while other people still need review. */
export interface KybReconciliationItem {
  id: number;
  applicationIndividualRoleId: number | null;
  applicationKybStakeholderId: number | null;
  action: KybReconciliationAction;
  /** True when the system selected this exact identity link without staff input. */
  isAutomatic: boolean;
  createdAt: string;
}

export interface KybReconciliationSummary {
  reconciliationGuid: string;
  applicationKybResultId: number;
  status: KybReconciliationStatus;
  fingerprint: string;
  completedByUserId: number | null;
  completedAt: string | null;
  carriedForwardFromReconciliationId: number | null;
  reviewNote: string | null;
  applicationPeople: KybReconciliationApplicationPerson[];
  automaticLinks: KybReconciliationAutomaticLink[];
  comparisons: KybReconciliationComparison[];
  actions: KybReconciliationItem[];
}

export interface KybReconciliationActionInput {
  action: KybReconciliationAction;
  /** Existing entity relationship used by KEEP/REMOVE, or the linked role when already present. */
  applicationIndividualRoleId?: number | null;
  /** Existing application person selected for LINK; the entity role is created or revived server-side. */
  applicationIndividualId?: number | null;
  applicationKybStakeholderId?: number | null;
  /** Complete confirmed profile required when a KYB-only stakeholder is added. */
  newPerson?: KybManualPerson | null;
}

export interface KybReconciliationSaveData {
  reconciliationGuid: string;
  expectedFingerprint: string;
  reviewNote?: string | null;
  actions: KybReconciliationActionInput[];
}

export interface KybReconciliationSaveResult {
  reconciliationGuid: string;
  saved: boolean;
  message: string | null;
}

/**
 * Shared FE/BE contract for the "Key People (KYB)" summary screen.
 *
 * Modeled on the KYB DB tables: a KYB result is per commercial entity
 * (ApplicationKYBResult), each result lists stakeholders (ApplicationKYBStakeholder).
 * A stakeholder has no ApplicationIndividual until it is saved, so the editable state is
 * keyed by stakeholderId. On save the stakeholder is materialised into an ApplicationIndividual
 * (isGuarantor) and ApplicationIndividualRole (isSigner, linked back via
 * applicationKybStakeholderId); individualId is backfilled onto KybPerson once that link exists.
 */

// Mirrors ApplicationKYBRun.status (the read model reports the owning run's status).
export type KybResultStatus =
  | 'INITIATING'
  | 'RETRY_PENDING'
  | 'IN_PROGRESS'
  | 'CALLBACK_RECEIVED'
  | 'RESULT_RETRY_PENDING'
  | 'COMPLETED'
  | 'SUPERSEDED'
  | 'INITIATION_FAILED'
  | 'RESULT_PROCESSING_FAILED';

/** UI-facing collapse of KybResultStatus. */
export type KybDisplayStatus = 'pending' | 'resolved' | 'failed';

// Mirrors ApplicationKYBStakeholder flag columns; a person may hold several roles.
export interface KybStakeholderRoleFlags {
  isDirector: boolean;
  isShareholder: boolean;
  isBeneficialOwner: boolean;
  isOfficeHolder: boolean;
  isTrustee: boolean;
  isPartner: boolean;
  isSoleTrader: boolean;
  isSecretary: boolean;
}

// One ApplicationKYBStakeholder joined to its ApplicationIndividual.
export interface KybPerson {
  stakeholderId: number;
  // The application person this stakeholder is, from ApplicationKYBStakeholderLink. Several
  // stakeholders can share one individualId when the registry lists a person more than once.
  individualId: number | null;
  frankieOneEntityId: string | null;
  givenName: string;
  middleName?: string | null;
  surname: string;
  dateOfBirth: string | null;
  // ApplicationKYBStakeholder.ownershipPct; null => not applicable ("-").
  ownershipPercent: number | null;
  roles: KybStakeholderRoleFlags;
  // <- ApplicationIndividual.isGuarantor (editable)
  isGuarantor: boolean;
  // <- ApplicationIndividualRole.isSigner (editable)
  isSigner: boolean;
  // The left-column tick, derived from whether an ApplicationIndividualRole row exists for the
  // stakeholder: absent => null (never saved, so the type-based default applies instead), live
  // => true, soft-deleted => false (a deliberate untick). There is no isSelected column.
  isSelected: boolean | null;
}

/**
 * The registry-sourced addresses a completed KYB run resolved for one entity, read from
 * ApplicationKYBAddress via the slot FKs on ApplicationKYBResult.
 *
 * Shaped as the address form control's value so the company details step can prefill it directly.
 * The registry does not always publish both, and an address it does publish may be missing
 * components, so either side can be null and individual fields can be empty.
 */
export interface KybEntityAddresses {
  registeredOffice: Address2ComponentValue;
  principalPlaceOfBusiness: Address2ComponentValue;
}

// One ApplicationKYBRun + its ApplicationCommercialEntity => one section. kybResultId is null while
// the run is still pending or has failed (the curated ApplicationKYBResult only exists once COMPLETED).
export interface KybEntityResult {
  kybResultId: number | null;
  commercialEntityId: number;
  entityName: string;
  entityType: EntityTypes;
  // <- ApplicationKYBResult.organizationTypeCode (ASIC type, e.g. APTY / APUB); drives the public
  // company exemption from the guarantor rules. Null while the run has not resolved.
  organizationTypeCode: string | null;
  abn: string | null;
  acn: string | null;
  isPrimary: boolean;
  isBorrower: boolean;
  isGuarantor: boolean;
  // Set when this entity is a corporate partner/trustee OF another entity, so the portal can match a
  // section to the partner/trustee row that created it.
  parentCommercialEntityId: number | null;
  isPartnerOfParent: boolean;
  isTrusteeOfParent: boolean;
  // ApplicationCommercialEntity.source, e.g. PREVIOUS_BUSINESS_ENTITY_SOURCE when this entity was
  // materialised from the application's Previous Business ABN rather than added on this step.
  source: string | null;
  status: KybResultStatus;
  mode: KybEntityMode;
  people: KybPerson[];
  manualPeople: KybManualPerson[];
  // Null until the run completes; the company details step prefills its address from the primary
  // entity's registeredOffice.
  addresses: KybEntityAddresses | null;
}

/**
 * Manual KYB is only a fallback while no curated FrankieOne result exists. A completed result that
 * returned no people may still use the same entry form, but must remain in automatic mode so it
 * cannot create reconciliation work.
 */
export function kybManualEntryRequiresManualMode(
  entity: Pick<KybEntityResult, 'kybResultId'>,
): boolean {
  return entity.kybResultId == null;
}

/** Restore manually entered people for a completed empty result without turning Manual KYB on. */
export function kybHasManualPeopleForEmptyResult(
  entity: Pick<KybEntityResult, 'kybResultId' | 'people' | 'manualPeople'>,
): boolean {
  return entity.kybResultId != null
    && entity.people.length === 0
    && entity.manualPeople.length > 0;
}

/**
 * Read model for one partner/trustee the user typed in on the KYB screen (Add Partner / Add
 * Trustee). Serialisable, so a date of birth is a YYYY-MM-DD string rather than a Moment.
 *
 * An individual is an ApplicationIndividual carrying the PortalKYB source; an entity is an
 * ApplicationCommercialEntity with that source, which also gets a KYB run of its own and therefore
 * appears in `entities` as well. `kind` comes from the isPartner/isTrustee flags on the role row
 * that links the party to its owning entity.
 */
export interface KybAddedIndividualPartyResult {
  type: 'Individual';
  kind: 'Partner' | 'Trustee';
  firstName: string | null;
  middleName: string | null;
  lastName: string | null;
  dateOfBirth: string | null;
}

export interface KybAddedEntityPartyResult {
  type: 'Entity';
  kind: 'Partner' | 'Trustee';
  legalName: string | null;
  abn: string | null;
  acn: string | null;
  entityType: EntityTypes | null;
}

export type KybAddedPartyResult = KybAddedIndividualPartyResult | KybAddedEntityPartyResult;

/** The persisted added parties of one partnership/trust entity, keyed by that owning entity. */
export interface KybAddedPartiesResult {
  commercialEntityId: number;
  parties: KybAddedPartyResult[];
}

/**
 * Top-level read payload (server -> portal): everything the portal knows about an application's KYB,
 * which is the entities themselves and their addresses as well as their people. Multiple entities
 * => multiple sections.
 */
export interface KybResult {
  applicationId: number;
  entities: KybEntityResult[];
  // Previously entered partners/trustees, so reopening a draft re-seeds the add-party forms rather
  // than showing them empty. Grouped by the entity they belong to; entities with none are omitted.
  addedParties: KybAddedPartiesResult[];
}

/**
 * Everything one corporate partner/trustee row's "Select signers" panel needs, resolved per row by
 * the container.
 *
 * The status travels with the entity because a run row exists (and so the entity is matchable) from
 * the moment it is initiated, long before its people arrive: without it the signers table cannot
 * tell "still waiting on the callback" from "the check returned nobody".
 *
 * The endpoint seams travel with it too, so the waiting panel can own its own wait exactly as the
 * company details step does. Carrying them here rather than as inputs on partner/trustee keeps all
 * the wiring in the container, which already builds this object per row.
 */
export interface KybSignerEntityView {
  entity: KybEntityResult;
  status: KybDisplayStatus;
  applicationId: number;
  pollFn: KybResultFn;
  refreshFn: KybRefreshFn | null;
}

/**
 * Write model (portal -> server): only guarantor/signer are editable per person.
 *
 * Keyed by stakeholderId (ApplicationKYBStakeholder.id), NOT individualId: a KYB stakeholder has
 * no ApplicationIndividual until it is saved, so the stakeholder is the stable handle. The save
 * flow materialises the ApplicationIndividual / ApplicationIndividualRole from the stakeholder.
 */
export interface KybPersonRoleSelection {
  stakeholderId: number;
  isGuarantor: boolean;
  isSigner: boolean;
  /**
   * Left-column select. Optional because the wizard still sends selection separately; when omitted,
   * validation falls back to the locked/default reading of the row.
   */
  isSelected?: boolean;
}

/**
 * One row's whole state after a tick, for callers that persist row by row rather than as a full
 * screen. The KYB wizard sends selection separately (selectedStakeholderIds) because it owns every
 * row; the KYC/AML tab shows only some rows, so each one has to carry its own selection or a save
 * could not tell an untouched row from a deselected one.
 */
export interface KybPersonRowState extends KybPersonRoleSelection {
  isSelected: boolean;
  /** Exact active application individual selected for a manual KYB stakeholder link. */
  applicationIndividualId?: number;
  /** Existing manual role selected by staff when this KYB person is linked instead of added. */
  applicationIndividualRoleId?: number;
}

/** Manually added partners/trustees for a partnership/trust entity (Add Partner / Add Trustee). */
export interface KybAddedParties {
  commercialEntityId: number;
  entityType: EntityTypes;
  parties: PartnerValue | TrusteeValue;
}

/**
 * CVA emit value of the kyb-key-people component; null until resolved / while invalid.
 * selectedStakeholderIds are the left-column ticked people; selections carry guarantor/signer.
 */
export interface KybKeyPeopleValue {
  selections: KybPersonRoleSelection[];
  selectedStakeholderIds: number[];
  addedParties: KybAddedParties[];
  manualEntities?: KybManualEntityValue[];
}

/** Save payload (portal -> server) for the "Key People (KYB)" screen. */
export interface KybKeyPeopleSaveData extends KybKeyPeopleValue {
  applicationId: number;
}

/** Endpoint seam, mirrors the BusinessNumberSearchFn injection pattern. */
export type KybResultFn = (applicationId: number) => Observable<KybResult>;

/** Persist the sticky manual-mode choice for one application entity and return the refreshed read model. */
export type KybSetManualModeFn = (
  applicationId: number,
  commercialEntityId?: number | null,
) => Observable<KybResult>;

/**
 * Endpoint seam for the Retry behind a KYB waiting panel. Unlike `KybResultFn` this has side
 * effects: it pulls the execution from FrankieOne rather than reading back what is already stored.
 *
 * `commercialEntityId` names the entity to reconcile, so the Key People step can retry one corporate
 * partner/trustee; omitting it reconciles the primary entity for the company details step.
 */
export type KybRefreshFn = (
  applicationId: number,
  commercialEntityId?: number | null,
) => Observable<KybResult>;

/** Request body of POST /initiate-corporate-party-kyb. */
export interface KybInitiateCorporatePartyInput {
  applicationId: number;
  // Omitted for a guarantor, which is an application-level party rather than an edge from an
  // owning entity the way a partner or trustee is.
  parentCommercialEntityId?: number;
  role: 'partner' | 'trustee' | 'guarantor';
  legalName: string | null;
  abn: string | null;
  acn: string | null;
  entityType: string | null;
}

export interface KybInitiateCorporatePartyResult {
  commercialEntityId: number;
  status: string | null;
}

/**
 * Endpoint seam for persisting a corporate party — a partner/trustee on Key People, or an entity
 * guarantor on Additional guarantors — and starting its KYB run, so its own key people can be
 * selected as signers while the user is still on the step.
 */
export type KybInitiateCorporatePartyFn =
  (input: KybInitiateCorporatePartyInput) => Observable<KybInitiateCorporatePartyResult>;

export function kybDisplayStatus(status: KybResultStatus): KybDisplayStatus {
  if (status === 'COMPLETED' || status === 'SUPERSEDED') {
    return 'resolved';
  }
  if (status === 'INITIATION_FAILED' || status === 'RESULT_PROCESSING_FAILED') {
    return 'failed';
  }
  // INITIATING / RETRY_PENDING / IN_PROGRESS / CALLBACK_RECEIVED / RESULT_RETRY_PENDING
  // (and any unknown) -> still working.
  return 'pending';
}

/** True only when every entity's KYB result has resolved. */
export function kybResultResolved(result: KybResult | null): boolean {
  if (!result || !result.entities.length) {
    return false;
  }
  return result.entities.every(e => kybDisplayStatus(e.status) === 'resolved');
}

/** True when any entity's KYB result has failed. */
export function kybResultFailed(result: KybResult | null): boolean {
  return !!result && result.entities.some(e => kybDisplayStatus(e.status) === 'failed');
}

const ROLE_LABELS: { key: keyof KybStakeholderRoleFlags; label: string }[] = [
  {key: 'isDirector', label: 'Director'},
  {key: 'isSecretary', label: 'Secretary'},
  {key: 'isShareholder', label: 'Shareholder'},
  {key: 'isBeneficialOwner', label: 'Beneficial Owner'},
  {key: 'isTrustee', label: 'Trustee'},
  {key: 'isPartner', label: 'Partner'},
  {key: 'isSoleTrader', label: 'Sole Trader'},
  {key: 'isOfficeHolder', label: 'Office Holder'},
];

/** Flags -> display labels; the section table joins these comma-separated. */
export function kybBusinessRoleLabels(roles: KybStakeholderRoleFlags): string[] {
  return ROLE_LABELS.filter(r => roles[r.key]).map(r => r.label);
}

/**
 * Seniority order the people table sorts on. Deliberately separate from ROLE_LABELS, which reads
 * left to right in the Business Role column and orders Shareholder and Beneficial Owner higher.
 * A person holding none of these (a trustee, partner or sole trader row) sorts below all of them.
 */
const ROLE_SORT_RANKS: (keyof KybStakeholderRoleFlags)[] = [
  'isDirector',
  'isSecretary',
  'isOfficeHolder',
  'isBeneficialOwner',
  'isShareholder',
];

const roleRank = (roles: KybStakeholderRoleFlags): number => {
  const rank = ROLE_SORT_RANKS.findIndex(key => roles[key]);
  return rank === -1 ? ROLE_SORT_RANKS.length : rank;
};

/**
 * Display order of the people table: highest-ranking business role first, then ownership from
 * highest to lowest within a rank (no ownership sorts last), then surname A-Z.
 *
 * Used by both the KYB wizard (including Select signers) and the KYC/AML tab.
 * Returns a new array so the caller's own ordering of the read model is left alone.
 */
export function kybSortKeyPeople(people: readonly KybPerson[]): KybPerson[] {
  return [...people].sort((a, b) => {
    const byRank = roleRank(a.roles) - roleRank(b.roles);
    if (byRank !== 0) {
      return byRank;
    }
    // Null ownership is not 0%: it means "not applicable", so it goes below every stated share.
    if (a.ownershipPercent !== b.ownershipPercent) {
      if (a.ownershipPercent == null) {
        return 1;
      }
      if (b.ownershipPercent == null) {
        return -1;
      }
      return b.ownershipPercent - a.ownershipPercent;
    }
    return (a.surname ?? '').localeCompare(b.surname ?? '');
  });
}

/** Section heading, adapted to entity type; additional (non-primary) entities are name-prefixed. */
export function kybSectionHeading(entityType: EntityTypes, isPrimary: boolean, entityName?: string): string {
  let base: string;
  switch (entityType) {
    case 'company':
      base = 'Beneficial Owners & Officeholders';
      break;
    case 'trust':
      base = 'Trustee';
      break;
    case 'partnership':
      base = 'Partners';
      break;
    case 'sole-trader':
      base = 'Business Owner & Office Holders';
      break;
    default:
      base = 'Members';
      break;
  }
  if (!isPrimary && entityName) {
    return `${entityName.toUpperCase()} - ${base}`;
  }
  return base;
}

/** Yellow warning shown under partnership/trust sections; null for other entity types. */
export function kybSectionWarning(entityType: EntityTypes): string | null {
  switch (entityType) {
    case 'partnership':
      return 'Please ensure all partners of this partnership are added in for faster processing of your application';
    case 'trust':
      return 'Please ensure all trustees of this trust are added in for faster processing of your application';
    default:
      return null;
  }
}

/**
 * Whether an entity's key people come from its own FrankieOne run.
 *
 * False for a trust and a partnership: neither has officers of its own to return, so their key
 * people are the trustees/partners typed in on the KYB step instead. Nothing on screen reads such a
 * run, so no step may wait on it - doing so would hide the form behind a check whose outcome is
 * never used. An unknown type is treated as having its own people, so the check is only skipped
 * where it is known to be pointless.
 */
export function kybHasOwnKeyPeople(entityType: EntityTypes | null): boolean {
  return entityType !== 'trust' && entityType !== 'partnership';
}

/** Whether a person's left-column select checkbox is ticked by default. */
export function kybIsSelectedByDefault(entityType: EntityTypes, ownershipPercent: number | null): boolean {
  if (entityType === 'company') {
    return ownershipPercent != null && ownershipPercent >= KYB_MAJOR_SHAREHOLDER_THRESHOLD;
  }
  if (entityType === 'sole-trader') {
    return true;
  }
  return false;
}

/**
 * Whether the entity is an additional guarantor rather than part of the borrowing structure, i.e. one
 * added on the Additional Guarantors step or materialised from the application's previous business.
 *
 * The KYB read feeds both that step and the Key People screen, so the two are told apart here rather
 * than by the query: Key People is the borrowing entity's signer selection and must leave guarantors
 * out, while the guarantors step needs exactly those rows. isPrimary/isBorrower are checked as well
 * so an entity carrying both flags is still treated as borrowing - dropping the applicant's own
 * entity from Key People would empty the step.
 */
export function kybIsAdditionalGuarantor(
  entity: Pick<KybEntityResult, 'isGuarantor' | 'isPrimary' | 'isBorrower'>,
): boolean {
  return entity.isGuarantor && !entity.isPrimary && !entity.isBorrower;
}

/**
 * Whether the entity is a corporate partner/trustee OF another entity, rather than a party of the
 * application in its own right.
 *
 * Such an entity is rendered inside the add-party rows of the entity that introduced it, so no
 * screen may also give it a top-level row of its own: its people would then appear twice. It cannot
 * be told apart by the guarantor flags, because a trustee of a guarantor inherits isGuarantor from
 * its owner (see initiateCorporatePartyKyb).
 */
export function kybIsCorporateParty(
  entity: Pick<KybEntityResult, 'isPrimary' | 'parentCommercialEntityId' | 'isPartnerOfParent' | 'isTrusteeOfParent'>,
): boolean {
  return !entity.isPrimary
    && entity.parentCommercialEntityId != null
    && (entity.isPartnerOfParent || entity.isTrusteeOfParent);
}

/** Whether a person's left-column select checkbox is locked (checked + greyed, non-interactive). */
export function kybIsSelectLocked(entityType: EntityTypes, ownershipPercent: number | null): boolean {
  if (entityType === 'company') {
    return ownershipPercent != null && ownershipPercent >= KYB_MAJOR_SHAREHOLDER_THRESHOLD;
  }
  return entityType === 'sole-trader';
}

export const KYB_MAJOR_SHAREHOLDER_LOCK_TOOLTIP =
  `Beneficial owners of ${KYB_MAJOR_SHAREHOLDER_THRESHOLD}% or more require ID verification for AML purposes.`;

export const KYB_MAJOR_SHAREHOLDER_REQUIRED_ERROR =
  `Beneficial owners of ${KYB_MAJOR_SHAREHOLDER_THRESHOLD}% or more must be selected for AML identity verification.`;

export const KYB_SOLE_TRADER_LOCK_TOOLTIP =
  `The sole trader must complete ID checks for AML purposes.`;

export const KYB_SIGNER_RESTRICTION_TOOLTIP =
  `Only a director, company secretary or office holder can be a signer.`;

export const KYB_SOLE_TRADER_ROLE_LOCK_TOOLTIP =
  `The sole trader must be both a guarantor and a signer.`;

/**
 * An individual added on the Additional Guarantors step is a guarantor by definition — that is the
 * only reason the step exists — so the tick is not something to reconsider row by row. Removing the
 * guarantor is the way to undo it.
 */
export const KYB_ADDED_GUARANTOR_LOCK_TOOLTIP =
  `You added this person as a guarantor. Remove the guarantor to undo this.`;

/** Signing is a power held over the borrowing entity, which an external guarantor does not have. */
export const KYB_ADDED_GUARANTOR_SIGNER_TOOLTIP =
  `Only a key person of an entity on this application can be a signer.`;

/**
 * The same individual was already made a guarantor on the Key People step, and isGuarantor belongs to
 * the individual rather than to one entity's row, so clearing it here would contradict that decision.
 */
export const KYB_GUARANTOR_ELSEWHERE_LOCK_TOOLTIP =
  `This person is already a guarantor on the Key People (KYB) screen.`;

/**
 * Popup shown when Next is pressed on an invalid Key People step. The step renders each failing
 * rule against the section it belongs to, so the popup points at those rather than naming the
 * kybKeyPeople control, which means nothing to a broker.
 */
export const KYB_KEY_PEOPLE_STEP_VALIDATION_MESSAGE =
  `Please review the items highlighted in red on this screen.`;

/**
 * Why Next is held when a completed company KYB returned nobody to tick. The signer rules cannot
 * be satisfied from an empty table, so the step points at Enter manually instead of "select a signer".
 */
export function kybEmptyPeopleManualEntryReason(entityNames: readonly string[]): string | null {
  if (!entityNames.length) {
    return null;
  }
  const names = entityNames.map(name => name.trim() || 'this entity').join(', ');
  return `No key people were returned for ${names}. Enter them manually to continue.`;
}

/** Why a person's left-column select checkbox cannot be changed; empty when it can. */
export function kybSelectLockTooltip(entityType: EntityTypes, ownershipPercent: number | null): string {
  if (!kybIsSelectLocked(entityType, ownershipPercent)) {
    return '';
  }
  return entityType === 'sole-trader' ? KYB_SOLE_TRADER_LOCK_TOOLTIP : KYB_MAJOR_SHAREHOLDER_LOCK_TOOLTIP;
}

/**
 * The left-column tick a person's row shows, from the strongest source available: a locked selection,
 * then any role tick (a guarantor or signer is necessarily a party), then what a previous save
 * recorded, then the entity-type default.
 *
 * A saved tick has to beat the default because the default is false for every unlocked row - the
 * ownership that makes it true also locks the row - so a person who is already a party would
 * otherwise read as unselected.
 */
export function kybResolveSelected(
  entityType: EntityTypes,
  person: Pick<KybPerson, 'ownershipPercent' | 'isGuarantor' | 'isSigner' | 'isSelected'>,
): boolean {
  if (kybIsSelectLocked(entityType, person.ownershipPercent)) {
    return kybIsSelectedByDefault(entityType, person.ownershipPercent);
  }
  if (person.isGuarantor || person.isSigner) {
    return true;
  }
  return person.isSelected ?? kybIsSelectedByDefault(entityType, person.ownershipPercent);
}

/** ASIC public companies are exempt from the guarantor rules. */
export function kybIsPublicCompany(organizationTypeCode: string | null | undefined): boolean {
  return (organizationTypeCode ?? '').trim().toUpperCase() === KYB_PUBLIC_COMPANY_TYPE_CODE;
}

/** Only directors, secretaries and other office holders may be nominated as signers of a company. */
export function kybIsSignerEligible(roles: KybStakeholderRoleFlags): boolean {
  return roles.isDirector || roles.isSecretary || roles.isOfficeHolder;
}

/**
 * Rows of the same company that are this application person, the row itself included. Only the link
 * (shared individualId) counts: two rows that merely share a name stay separate people.
 */
export function kybLinkedRowsOnCompany(people: readonly KybPerson[], person: KybPerson): KybPerson[] {
  return person.individualId == null
    ? [person]
    : people.filter(other => other.stakeholderId === person.stakeholderId || other.individualId === person.individualId);
}

/** Signer eligibility of the person behind a row: they sign once for the company, under whichever role. */
export function kybIsPersonSignerEligible(people: readonly KybPerson[], person: KybPerson): boolean {
  return kybLinkedRowsOnCompany(people, person).some(row => kybIsSignerEligible(row.roles));
}

/** A sole trader is the business itself, so they always guarantee and sign for it. */
export function kybIsSoleTraderRoleLocked(entityType: EntityTypes | null | undefined): boolean {
  return entityType === 'sole-trader';
}

const entityTypeOption = (entityType: EntityTypes | null): Exclude<EntityTypeValue, null> =>
  EntityTypeValueOptions.find(option => option.type === entityType) ?? EntityTypeCompanyOption;

/**
 * Rebuild the Add Partner / Add Trustee form value from the parties that were persisted, so
 * reopening a draft shows them instead of an empty form. Returns null when nothing was saved for
 * this entity, which is what leaves the form on its own defaults.
 *
 * Only the fields the KYB screen renders are set (name + dob for an individual, the organisation for
 * an entity); it strips the controls for address, contact and assets/liabilities, which are
 * collected on the dedicated party detail page later in the flow.
 *
 * An entity always comes back as a resolved-organisation row: nothing records whether it was found
 * by ABN/ACN lookup or typed in by hand, and the lookup shape is the one both forms accept.
 *
 * Every party is restored regardless of the role it was saved under, and re-labelled for the form it
 * is going into. An entity whose type changed between visits (partnership -> trust, say) therefore
 * keeps the people that were entered instead of silently dropping them, which is also what the forms
 * themselves do: both stamp their own `kind` onto every row they build.
 */
export function kybAddedPartiesToPartyValue(
  parties: readonly KybAddedPartyResult[],
  entityType: EntityTypes,
): PartnerValue | TrusteeValue {
  const kind: 'Partner' | 'Trustee' = entityType === 'trust' ? 'Trustee' : 'Partner';
  const rows: Record<string, unknown>[] = [];
  for (const party of parties) {
    if (party.type === 'Individual') {
      rows.push({
        kind,
        type: 'Individual',
        firstName: party.firstName ?? '',
        middleName: party.middleName ?? '',
        lastName: party.lastName ?? '',
        // The datepicker works in Moments; parsed as a calendar day so the date shown is the one
        // that was entered rather than one shifted by the local timezone.
        dob: party.dateOfBirth ? moment(party.dateOfBirth, 'YYYY-MM-DD') : null,
      });
    } else {
      rows.push({
        kind,
        type: 'Entity',
        manualEntry: false,
        organisation: {
          acn: party.acn ?? '',
          abn: party.abn ?? '',
          name: party.legalName ?? '',
          displayName: party.legalName ?? '',
        },
        organisationType: entityTypeOption(party.entityType),
        abn: party.abn ?? '',
        acn: party.acn ?? '',
      });
    }
  }
  // The partial rows above are what the KYB forms read back, but they do not satisfy the full
  // partner/trustee types, whose remaining fields belong to the non-KYB version of these forms.
  return rows.length ? (rows as unknown as Exclude<PartnerValue, null>) : null;
}

/** ABN/ACN of one add-party row, keyed as digits so a formatted and a raw entry match. */
export interface KybPartyIdentifiers {
  abn: string | null;
  acn: string | null;
}

/**
 * Registration numbers as bare digits, so a value formatted for display matches the same number
 * stored raw. Null for anything with no digits at all, which is what "no identifier" means here.
 */
export function kybDigitsOnly(value: unknown): string | null {
  const digits = (value ?? '').toString().replace(/\D/g, '');
  return digits || null;
}

/**
 * ABN/ACN of one partner/trustee party, however it was entered. A manually entered row carries only
 * an ACN and deliberately yields no ABN. The live form carries that ACN in `stepacn`; the trustee
 * component's normalised change event carries the same value in `organisation.acn`.
 */
export function kybPartyIdentifiers(party: unknown): KybPartyIdentifiers {
  const row = party as {
    manualEntry?: boolean;
    stepacn?: string;
    organisation?: { abn?: string; acn?: string };
  } | null;
  return {
    abn: kybDigitsOnly(row?.manualEntry ? null : row?.organisation?.abn),
    acn: kybDigitsOnly(row?.manualEntry
      ? row?.stepacn ?? row?.organisation?.acn
      : row?.organisation?.acn),
  };
}

/**
 * Integrity errors of one partner/trustee row against the applicant's primary entity. Individuals
 * have no entity to check, so they never fail. Run before a row starts a KYB run, so an invalid
 * entity is stopped at entry instead of being saved and failing a later full save.
 */
export function kybPartyEntryIntegrityErrors(
  entry: unknown,
  role: 'partner' | 'trustee',
  primary: KybPartyIdentifiers | null | undefined,
): { code: CommercialEntityIntegrityErrorCode; message: string }[] {
  const row = entry as { type?: string; organisationType?: { type?: string } | null } | null;
  if (row?.type !== 'Entity') {
    return [];
  }
  const {abn, acn} = kybPartyIdentifiers(entry);
  return validateKybPartyIntegrity({role, abn, acn, entityType: row.organisationType?.type}, primary);
}

/**
 * The KYB entity created for one add-party row of `parentCommercialEntityId`, matched on whichever
 * identifier the row carries. Null while the row has no usable ABN/ACN, or before its run exists.
 *
 * Scoped to the parent so two rows for the same company under different parents stay distinct, and
 * so a row never adopts the entity of an unrelated section.
 */
export function kybFindEntityForParty(
  entities: readonly KybEntityResult[],
  parentCommercialEntityId: number,
  identifiers: KybPartyIdentifiers,
): KybEntityResult | null {
  const {abn, acn} = identifiers;
  if (!abn && !acn) {
    return null;
  }
  return entities.find(candidate =>
    candidate.parentCommercialEntityId === parentCommercialEntityId &&
    ((!!acn && kybDigitsOnly(candidate.acn) === acn) || (!!abn && kybDigitsOnly(candidate.abn) === abn)),
  ) ?? null;
}

/**
 * dob comes off the datepicker as a Moment at local midnight. JSON serialisation of a Moment uses
 * toISOString() (UTC), which shifts the calendar date back a day for AEST/AEDT and is then persisted
 * off-by-one. Emit dob as a local YYYY-MM-DD string so the saved date matches what was entered.
 */
export function kybNormalizePartyDobForSave(parties: PartnerValue | TrusteeValue): PartnerValue | TrusteeValue {
  if (!parties) {
    return parties;
  }
  return parties.map(party => {
    const dob = (party as { type?: string; dob?: unknown }).dob;
    if ((party as { type?: string }).type !== 'Individual' || dob == null) {
      return party;
    }
    const m = moment.isMoment(dob) ? dob : moment(dob as moment.MomentInput);
    return m.isValid() ? {...party, dob: m.format('YYYY-MM-DD')} : party;
  }) as PartnerValue | TrusteeValue;
}

export interface KybValidationResult {
  errors: string[];
  warnings: string[];
}

/**
 * Non-blocking copy: sub-50% guarantor coverage is allowed but flagged for manual review. Single
 * location so the wording can be changed once.
 */
export const KYB_GUARANTEE_COVERAGE_WARNING =
  'Guarantees covering at least 50% of company ownership have not been selected. ' +
  'Application will require manual assessment.';

/** The fields that decide whether two KYB rows are the same human. */
export type KybPersonIdentity =
  Pick<KybPerson, 'individualId' | 'frankieOneEntityId' | 'givenName' | 'middleName' | 'surname' | 'dateOfBirth'>;

/**
 * Whether two rows are the same human.
 *
 * The same person is often a stakeholder of several entities on one application - the borrowing
 * company and an additional guarantor, say - and holds a single ApplicationIndividual across all of
 * them, so person-level state (isGuarantor) has to be reconciled across their rows instead of being
 * held per row.
 *
 * Compared pairwise rather than through one identity key, because the two sides rarely carry the
 * same identifiers: a stakeholder an earlier save materialised has an individualId that a freshly
 * returned one does not, and two KYB runs can describe the same person under different FrankieOne
 * entity ids. Two saved individualIds decide either way. A shared FrankieOne entity id proves the same
 * person, but a different one does not prove a different person, so it falls through to the name and
 * date-of-birth rule.
 */
export function kybIsSamePerson(a: KybPersonIdentity, b: KybPersonIdentity): boolean {
  if (a.individualId != null && b.individualId != null) {
    return a.individualId === b.individualId;
  }
  if (a.frankieOneEntityId && a.frankieOneEntityId === b.frankieOneEntityId) {
    return true;
  }
  const match = compareIdentity(a, b);
  return match === 'exact' || match === 'name-one-dob';
}

/**
 * Which stakeholders on the Additional Guarantors step already guarantee through the Key People
 * step, so that step's decision can be shown there ticked and locked.
 *
 * isGuarantor belongs to the individual, not to a row, and the same human is regularly a key person
 * of both the borrowing company and an additional guarantor. Left independent, the two tables would
 * disagree over one flag and the save - which writes the guarantors step's stakeholders last - would
 * clear a guarantee taken on Key People.
 *
 * @param shownEntities the entities whose people the guarantors step renders ticks for.
 * @param allEntities every entity on the application; those outside `shownEntities` are the ones
 *   Key People answers for.
 * @param keyPeopleSelections that step's live value, which decides over the persisted flag so a tick
 *   the broker has just cleared does not leave a locked one behind. Null where it is not available
 *   (direct sale has no Key People step), leaving the persisted flag to decide.
 */
export function kybGuarantorsHeldByKeyPeople(
  shownEntities: readonly KybEntityResult[],
  allEntities: readonly KybEntityResult[],
  keyPeopleSelections: KybKeyPeopleValue | null,
): Set<number> {
  const shownIds = new Set(shownEntities.map(entity => entity.commercialEntityId));
  const selections = new Map((keyPeopleSelections?.selections ?? []).map(s => [s.stakeholderId, s]));
  // The Key People save drops the roles of an unselected stakeholder, so their tick is not one to
  // hold the guarantors step to either.
  const selectedIds = new Set(keyPeopleSelections?.selectedStakeholderIds ?? []);
  const guarantors = allEntities
    .filter(entity => !shownIds.has(entity.commercialEntityId))
    .flatMap(entity => entity.people)
    .filter(person => {
      const selection = selections.get(person.stakeholderId);
      return selection
        ? selection.isGuarantor && selectedIds.has(person.stakeholderId)
        : person.isGuarantor;
    });

  const held = new Set<number>();
  for (const entity of shownEntities) {
    for (const person of entity.people) {
      if (guarantors.some(guarantor => kybIsSamePerson(guarantor, person))) {
        held.add(person.stakeholderId);
      }
    }
  }
  return held;
}

export interface KybKeyPeopleValidationOptions {
  /**
   * Drop the guarantor rules (the director-guarantor requirement and the coverage warning) while
   * keeping the signer ones. Set for an additional guarantor entity: the guarantee it gives is the
   * broker's commercial decision, so its people are collected without being held to the guarantor
   * cover the applicant's own company must show on Key People.
   */
  skipGuarantorRules?: boolean;
}

/**
 * Signer and guarantor rules for one company KYB section. Pure, so the portal gate and the save path
 * share one copy of the rules.
 *
 * `selections` are the in-flight guarantor/signer ticks keyed by stakeholderId; a person with no
 * selection falls back to the flags already persisted on the row, which is what lets the server
 * validate straight off the read model.
 */
export function kybValidateCompanyKeyPeople(
  entity: KybEntityResult,
  entityType: EntityTypes,
  selections: readonly KybPersonRoleSelection[] = [],
  options: KybKeyPeopleValidationOptions = {},
): KybValidationResult {
  const errors: string[] = [];
  const warnings: string[] = [];
  // Trusts and partnerships use their dedicated party forms; corporate parties are validated here
  // as companies. Sole traders have their selection and roles locked on instead of a rule set.
  if (entityType !== 'company') {
    return {errors, warnings};
  }

  const byStakeholder = new Map(selections.map(s => [s.stakeholderId, s]));
  const rows = entity.people.map(person => {
    const selection = byStakeholder.get(person.stakeholderId);
    return {
      person,
      isSelected: selection?.isSelected ?? kybResolveSelected(entityType, person),
      isGuarantor: selection?.isGuarantor ?? person.isGuarantor,
      isSigner: selection?.isSigner ?? person.isSigner,
    };
  });

  // Linked rows of one person share a single role on the company and a single guarantor flag, and the
  // save keeps that role while any of their rows is selected or signing. So the rules judge them
  // together: the roles of all their rows, and a tick on any of them. Only the link joins rows here,
  // as on the table; unlinked rows are saved separately, so each is judged on its own.
  const linkKey = (person: KybPerson): string =>
    person.individualId != null ? `i:${person.individualId}` : `s:${person.stakeholderId}`;
  // The same human can still be several unlinked rows (e.g. director and shareholder rows under two
  // FrankieOne entity ids), so counts and coverage are taken over unique people rather than rows.
  // A row without a date of birth can match two people who differ on it, so people with one are
  // grouped first, and a row without one joins a person only when it matches exactly one.
  const linkKeys = [...new Set(rows.map(row => linkKey(row.person)))];
  const firstPersonOf = (key: string): KybPerson => rows.find(row => linkKey(row.person) === key)!.person;
  const hasDob = (key: string): boolean => !!(firstPersonOf(key).dateOfBirth ?? '').trim();
  const representatives: KybPerson[] = [];
  const identityByLinkKey = new Map<string, number>();
  for (const key of [...linkKeys.filter(hasDob), ...linkKeys.filter(key => !hasDob(key))]) {
    const subject = firstPersonOf(key);
    const matches = representatives.flatMap((seen, index) => kybIsSamePerson(seen, subject) ? [index] : []);
    const joins = hasDob(key) ? matches.length >= 1 : matches.length === 1;
    identityByLinkKey.set(key, joins ? matches[0] : representatives.push(subject) - 1);
  }
  const people = linkKeys.map(key => {
    const own = rows.filter(row => linkKey(row.person) === key);
    const anyRole = (role: keyof KybStakeholderRoleFlags) => own.some(row => row.person.roles[role]);
    return {
      rows: own,
      identity: identityByLinkKey.get(key)!,
      isDirector: anyRole('isDirector'),
      isSignerEligible: own.some(row => kybIsSignerEligible(row.person.roles)),
      isSelected: own.some(row => row.isSelected),
      isGuarantor: own.some(row => row.isGuarantor),
      isSigner: own.some(row => row.isSigner),
      // One person can be several registry rows with different holdings; the largest stands for them.
      ownershipPercent: Math.max(0, ...own.map(row => row.person.ownershipPercent ?? 0)),
    };
  });

  const count = (predicate: (person: typeof people[number]) => boolean): number =>
    new Set(people.filter(predicate).map(person => person.identity)).size;
  const eligible = count(person => person.isSignerEligible);
  const signers = count(person => person.isSigner);
  const directors = count(person => person.isDirector);
  const directorSigners = count(person => person.isDirector && person.isSigner);
  const directorGuarantors = count(person => person.isDirector && person.isGuarantor);

  const unselectedRequired = people.filter(person =>
    !person.isSelected && person.rows.some(row => kybIsSelectLocked(entityType, row.person.ownershipPercent)));
  if (unselectedRequired.length) {
    const names = [...new Set(unselectedRequired.map(person => {
      const name = `${person.rows[0].person.givenName ?? ''} ${person.rows[0].person.surname ?? ''}`.trim();
      return name || 'This person';
    }))].join(', ');
    errors.push(`${names}: ${KYB_MAJOR_SHAREHOLDER_REQUIRED_ERROR}`);
  }

  if (people.some(person => person.isSigner && !person.isSignerEligible)) {
    errors.push(`Only a director, company secretary or office holder can be a signer for ${entity.entityName}.`);
  }

  // An entity with a single eligible signer cannot reach two signers, so the minimum relaxes
  // rather than being unsatisfiable.
  const requiredSigners = eligible <= 1 ? 1 : KYB_MIN_SIGNERS;
  if (signers < requiredSigners) {
    errors.push(requiredSigners === 1
      ? `Select at least 1 signer for ${entity.entityName}.`
      : `Select at least ${requiredSigners} signers for ${entity.entityName}.`);
  }
  // Only required when a director exists, so a secretary-only entity stays satisfiable.
  if (directors > 0 && directorSigners === 0) {
    errors.push(`At least one signer for ${entity.entityName} must be a director.`);
  }

  if (options.skipGuarantorRules || kybIsPublicCompany(entity.organizationTypeCode)) {
    return {errors, warnings};
  }

  if (directors > 0 && directorGuarantors === 0) {
    errors.push(`At least one director of ${entity.entityName} must be a guarantor.`);
  }

  // Ownership-weighted guarantee coverage, counting each guarantor once and null ownership as 0.
  const guarantorOwnership = new Map<number, number>();
  for (const person of people.filter(candidate => candidate.isGuarantor)) {
    guarantorOwnership.set(person.identity, Math.max(guarantorOwnership.get(person.identity) ?? 0, person.ownershipPercent));
  }
  const coverage = Array.from(guarantorOwnership.values()).reduce((sum, pct) => sum + pct, 0);
  if (coverage < KYB_GUARANTEE_COVERAGE_THRESHOLD) {
    warnings.push(KYB_GUARANTEE_COVERAGE_WARNING);
  }

  return {errors, warnings};
}
